import { autorun } from "mobx"
import { flow, getRoot, getSnapshot, Instance, SnapshotOut, types, detach } from "mobx-state-tree"
import { syncAppBadge } from "~services/jpush/badge"
import {
  Event,
  EventNames,
  FLAG_ACK,
  MessageBatchStatusUpdatedData,
  MessageData,
  MessageStatus,
  MessageStatusUpdatedData,
  MessageBatchNewData,
  MessageDeleteData,
  MessageTypes,
  MessageBatchReadedData,
  MessageBatchDeleteData,
  GameChatUnreadSummaryData,
} from "~proto"
import { NotifyType } from "~types/message"
import { messageSentSound } from "~sounds"
import { debounceLeading, hapticTrigger } from "~utils"
import { newID, genPrivacyChatID } from "~utils/id"
import { withEnvironment } from "../extensions/with-environment"
import type { Chat } from "../chat/chat"
import { Message, MessageModel } from "../message/message"
import { RootStoreModel } from "~models/root-store"
import {
  queryMessageByMessageID,
  updateMessageByMessageID,
  deleteMessageByMessageID,
  deleteMessagesByChatID,
  deleteMessagesByMessageIDs,
  insertMessage,
} from "~services/sqlite"
import { TransferStatus, RedpacketStatus } from "~proto"

// 收到新消息的特效
const newMessageEffect = debounceLeading(() => {
  hapticTrigger()
})

/**
 * GAME_CHAT_UNREAD_SUMMARY 可能一次性下发很多游戏群（每群又各有多条未读），
 * 全部同步处理会阻塞触摸与掉帧。按「每帧处理的未读消息条数预算」分片，超出后让出主线程。
 */
const GAME_UNREAD_MESSAGE_CHUNK_BUDGET = 200

/** 非当前会话在内存中最多保留的消息条数（与首屏窗口 limit=30 一致，重进秒出一屏） */
const KEEP_PER_INACTIVE_CHAT = 30
/** 滞后水位：超过 KEEP+SLACK 才裁回 KEEP，把裁剪时的排序成本摊薄到每 SLACK 条一次 */
const TRIM_SLACK = 20

/**
 * 消息存储
 */
export const MessageStoreModel = types
  .model("MessageStore")
  .props({
    messages: types.optional(types.map(MessageModel), {}),
  })
  .volatile(() => ({
    sendMessageTimeoutMessageIdMap: {},
  }))
  .extend(withEnvironment)
  .views((self) => ({
    // 别人发的 + 我没读 + 非通知消息 的总数
    get unread() {
      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      let n = 0
      // let n = Array.from(self.messages.values()).filter((message) => {
      //   const chat = chatStore.getChat(message.messageChatID)
      //   return !chat?.deleted && !message.byMe && !message.isReadedByMe && !message.notify_type
      // }).length
      chatStore.chats.forEach((chat) => {
        if (!chat.deleted) n += (chat?.is_mute ? 0 : chat.messageUnreadCount)
      })
      return n
    },
  }))
  .actions((self) => {
    function clear() {
      self.messages.clear()
    }
    function ensureMessageData(message: Partial<MessageData>) {
      return {
        ...message,
        mention_ids: message.mention_ids || [],
        read_ids: message.read_ids || [],
      }
    }
    function sanitizeMessageData(message: Partial<MessageData>) {
      const m = { ...message } as Partial<MessageData> & Record<string, unknown>
      if (m.message_id != null) m.message_id = String(m.message_id)
      if (m.content == null) m.content = ""
      if (m.type == null || !Object.values(MessageTypes).includes(m.type as MessageTypes)) {
        m.type = MessageTypes.TEXT
      }
      if (m.status == null || !Object.values(MessageStatus).includes(m.status as MessageStatus)) {
        m.status = MessageStatus.SENT
      }
      if (m.id != null && typeof m.id !== "number") {
        m.id = /^\d+$/.test(String(m.id)) ? Number(m.id) : null
      }
      if (m.sid != null && typeof m.sid !== "number") {
        m.sid = /^\d+$/.test(String(m.sid)) ? Number(m.sid) : null
      }
      const now = Date.now()
      if (!(m.created instanceof Date)) {
        const n = Number(m.created)
        m.created = Number.isFinite(n) && n > 0 ? new Date(n) : new Date(now)
      }
      if (!(m.updated instanceof Date)) {
        const n = Number(m.updated)
        m.updated = Number.isFinite(n) && n > 0 ? new Date(n) : new Date(now)
      }
      if (m.attach === null || (Array.isArray(m.attach) && m.attach.length === 0)) {
        m.attach = undefined
      }
      return m
    }
    function ensureMessageUserRefs(message: Partial<MessageData>) {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const extra = message as Partial<MessageData> & {
        sender?: number | { id?: number } | null
        receiver?: number | { id?: number } | null
      }

      const resolveId = (
        id?: number | null,
        ref?: number | { id?: number } | null,
      ) => id ?? (typeof ref === "number" ? ref : ref?.id ?? null)

      const ensureUser = (userId: number | null, displayName?: string | null) => {
        if (!userId) return
        const key = userId.toString()
        if (userStore.users.has(key)) return
        userStore.addUser({
          id: userId,
          username: String(userId),
          name: displayName?.trim() || `用户${userId}`,
        })
      }

      ensureUser(resolveId(message.sender_id, extra.sender), message.sender_name ?? null)
      ensureUser(resolveId(message.receiver_id, extra.receiver), message.receiver_name ?? null)
    }
    function repairChatMessageUsers(chat: Chat) {
      chat.messages.forEach((message) => {
        const snap = getSnapshot(message) as { sender?: number | null; receiver?: number | null }
        if (!message.sender && typeof snap.sender === "number") {
          ensureMessageUserRefs({ sender_id: snap.sender })
        }
        if (!message.receiver && typeof snap.receiver === "number") {
          ensureMessageUserRefs({ receiver_id: snap.receiver })
        }
      })
    }
    // 將後端格式轉為前端格式
    function messageDataToMessage(message: Partial<MessageData>) {
      message = sanitizeMessageData(ensureMessageData(message))
      const { sender_id, receiver_id, sender, receiver, ...rest } = message as Partial<
        MessageData & {
          sender?: number | { id?: number } | null
          receiver?: number | { id?: number } | null
        }
      >
      const resolvedSenderId =
        sender_id ??
        (typeof sender === "number" ? sender : sender?.id ?? null)
      const resolvedReceiverId =
        receiver_id ??
        (typeof receiver === "number" ? receiver : receiver?.id ?? null)
      const cM = {
        ...rest,
        receiver: resolvedReceiverId || null,
        sender: resolvedSenderId || null,
      }
      return cM
    }
    function preserveLocalAttachData(message: Partial<MessageData>) {
      const localMessage = self.messages.get(message.message_id)
      if (!localMessage) return message
      const cM = {
        ...localMessage,
        ...message,
        attach_path: localMessage.attach_path,
      }
      // 红包/转账的业务状态会在“点开详情”后写入本地 sqlite。
      // 重新登录时服务端的 last-message / history 下发可能仍是旧状态，
      // 这里保护本地已更新过的终态，避免被服务端旧数据回滚。
      if (message.type === MessageTypes.NEW_TRANS) {
        const localStatus = localMessage.attach?.trans_info?.status
        const incomingStatus = (message as any)?.attach?.trans_info?.status
        // 本地已是终态（非 PENDING），且服务端仍是 PENDING/缺失时，保留本地
        if (
          localStatus != null &&
          localStatus !== TransferStatus.PENDING &&
          (incomingStatus == null || incomingStatus === TransferStatus.PENDING)
        ) {
          cM.attach = {
            ...(message as any)?.attach,
            ...(localMessage.attach as any),
            trans_info: {
              ...(message as any)?.attach?.trans_info,
              ...(localMessage.attach as any)?.trans_info,
            },
          }
        }
      }
      if (message.type === MessageTypes.RED_PACKET) {
        const localStatus = localMessage.attach?.redpacket_info?.status
        const incomingStatus = (message as any)?.attach?.redpacket_info?.status
        // 本地已是终态（非 ACTIVE），且服务端仍是 ACTIVE/缺失时，保留本地
        if (
          localStatus != null &&
          localStatus !== RedpacketStatus.ACTIVE &&
          (incomingStatus == null || incomingStatus === RedpacketStatus.ACTIVE)
        ) {
          cM.attach = {
            ...(message as any)?.attach,
            ...(localMessage.attach as any),
            redpacket_info: {
              ...(message as any)?.attach?.redpacket_info,
              ...(localMessage.attach as any)?.redpacket_info,
            },
          }
        }
      }
      if (message.type === MessageTypes.VIDEO) {
        cM.attach = {
          ...localMessage.attach,
          ...message.attach,
        }
      }
      if (message.type === MessageTypes.IMAGES) {
        cM.attach = {
          ...localMessage.attach,
          ...message.attach,
          imgs: localMessage.attach.imgs.map((img, i) => {
            return {
              ...img,
              ...message.attach.imgs[i],
            }
          }),
        }
      }
      return cM
    }
    const getMessage = (message_id: string) => {
      return self.messages.get(message_id.toString())
    }
    const putMessage = (data: Partial<MessageData>) => {
      if (!data?.message_id) return null
      try {
        ensureMessageUserRefs(data)
        const adaptedMessage = messageDataToMessage(data)
        const preservedLocalDataMessage = preserveLocalAttachData(adaptedMessage)
        return self.messages.put(preservedLocalDataMessage)
      } catch (e) {
        if (__DEV__) {
          console.warn("[message-store] skip invalid message", data.message_id, e)
        }
        return null
      }
    }
    const addMessage = (message: Partial<MessageData>) => {
      const now = Date.now()
      return putMessage({
        message_id: message.message_id || newID(),
        created: now,
        updated: now,
        ...message,
      })
    }
    const getMessageInDB = flow(function* (message_id: string) {
      const result = yield queryMessageByMessageID(message_id)
      return result
    })
    const flushReplyMessagesByIds = flow(function* (replyIds: string[]) {
      for (let i = 0; i < replyIds.length; i++) {
        const reply_msg_id = replyIds[i]
        const replyMessageData = yield getMessageInDB(reply_msg_id)
        if (replyMessageData) putMessage(replyMessageData)
      }
    })
    const hasMessageInDB = flow(function* (message_id: string) {
      const result = yield queryMessageByMessageID(message_id)
      return !!result
    })
    const putMessageInDB = flow(function* (data: Partial<MessageData>) {
      data = ensureMessageData(data)
      yield insertMessage({ cM: data })
      return data
    })
    const onHistoryMessageCreated = (data: MessageData) => {
      messageCreatedHandle(data, true)
    }
    const onLastMessageCreated = (data: MessageData) => {
      messageCreatedHandle(data, true)
    }
    const onMessageCreated = ({ data }: Event<MessageData>) => {
      messageCreatedHandle(data, false)
    }
    const messageCreatedHandle = flow(function* (
      data: MessageData & { notify_type?: NotifyType },
      prepend?: boolean,
      options?: { silentEffects?: boolean },
    ) {
      // put message to memory and database
      if (!data.message_id || !data.sender_id) {
        // console.warn("bad data here!", data)
        return
      }
      const { chatStore, userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { currentUser } = userStore
      const other_id = currentUser.id === data.sender_id ? data.receiver_id : data.sender_id
      // console.log("onMessage---", data)
      if (!data.chat_cid) {
        if (data.chat_id != null) {
          const groupChat = chatStore.byId(data.chat_id)
          if (groupChat) data.chat_cid = groupChat.chat_id
        }
        if (!data.chat_cid) {
          const other_id = currentUser.id === data.sender_id ? data.receiver_id : data.sender_id
          data.chat_cid = genPrivacyChatID(currentUser.id, other_id).chat_id
        }
      }

      const message = putMessage(data)
      if (!message) return
      if (!data.notify_type) putMessageInDB(data)

      const chat = chatStore.getChatByMessageRef(data) ?? chatStore.getChat(message.messageChatID)

      // 实时新消息到来时，如果该会话被本机软删除过，需要同时：
      // 1) 把 chat.deleted 复位
      // 2) 从 deletedChatIds + 持久化 DELETED_CHAT_IDS 中移除
      // 否则下次 onChatList 合并本地删除列表时会再次被标记为 deleted。
      if (chat && !prepend && chat.deleted) {
        chatStore.reviveChat(chat)
      }

      // insert message to chat render list.
      // 历史/最后一条消息同步（prepend=true）不要复活已删除会话；实时新消息则允许复活
      if (chat) {
        chat.onMessageCreated(message, prepend, { reviveDeletedChat: !prepend })
        // 非当前会话超水位即裁剪，防止后台高频群把内存越堆越大（当前会话/历史加载不受影响）
        trimInactiveChatMessages(chat)
      }

      if (!options?.silentEffects && message.sender?.id !== currentUser.id) {
        newMessageEffect()
      }
      // if message has reply message, we will find it from database and insert it to memory, to make sure we can render it.
      if (message.reply_msg_id) {
        const replyMessageData = yield getMessageInDB(message.reply_msg_id)
        if (replyMessageData) putMessage(replyMessageData)
      }
    })
    /**
     * 非当前会话的内存淘汰：messageStore 全局缓存原本只进不出，长时间挂机（尤其高频游戏群）
     * 会无界增长 → GC 压力上升、慢性内存膨胀。这里在消息入列后对「非当前会话」做裁剪：
     *   - 超过 KEEP+SLACK 水位才触发、一次裁回 KEEP（滞后水位，摊薄排序成本）；
     *   - 当前会话不裁（用户可能上拉加载了历史）；
     *   - chat.trimToLatest 负责渲染列表收缩/未读折算，这里把淘汰的消息从全局缓存销毁
     *     （safeReference 语义下两者必须同步进行）。
     * 重进会话不丢数据：普通群从 SQLite 读回，游戏群进群直接拉服务端最新一屏。
     */
    function trimInactiveChatMessages(chat?: Chat | null) {
      if (!chat) return
      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      if (chatStore.current?.chat_id === chat.chat_id) return
      if (chat.messages.length <= KEEP_PER_INACTIVE_CHAT + TRIM_SLACK) return
      const evictedIds = chat.trimToLatest(KEEP_PER_INACTIVE_CHAT)
      evictedIds.forEach((id) => remove(id))
    }

    /**
     * 批量下发路径（EVENT_PACKED / GAME_CHAT_UNREAD_SUMMARY）共用的延后副作用：
     *   - SQLite 写入收敛为单条串行链，避免数千并发 INSERT
     *   - 回复链补齐去重后用单个 flow 统一处理
     */
    function flushPackedSideEffects(pendingSqLite: MessageData[], replyIdsToHydrate: Set<string>) {
      if (pendingSqLite.length) {
        void pendingSqLite
          .reduce(
            (chain, snap) => chain.then(() => insertMessage({ cM: snap })),
            Promise.resolve() as Promise<unknown>,
          )
          .catch(() => {})
      }

      if (replyIdsToHydrate.size > 0) {
        flushReplyMessagesByIds(Array.from(replyIdsToHydrate))
      }
    }

    /**
     * EVENT_PACKED 内的一批 MESSAGE_CREATED：与 onGameChatUnreadSummary 同样的批处理思路，
     * 避免逐条走 messageCreatedHandle 时为每条消息各起一个 flow（SQLite insert / 回复链补齐）以及
     * 对会话渲染列表逐条 unshift 造成的 O(batch×列表长度) 开销——几千条未读时会阻塞触摸与掉帧。
     *   - putMessage 同步批量写入内存
     *   - SQLite 写入延后为单条串行链，避免数千并发 INSERT
     *   - 回复链补齐去重后用单个 flow 统一处理
     *   - 每个会话一次性 prepend，静默触感
     */
    function onPackedMessagesCreated(rawMessages: (MessageData & { notify_type?: NotifyType })[]) {
      if (!rawMessages?.length) return

      const { chatStore, userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { currentUser } = userStore

      /** 延后写入 SQLite（串行）；避免本条路径对每条起一个 flow + 数千并发 INSERT */
      const pendingSqLite: MessageData[] = []
      /** 延后单条 flow 补齐回复链，避免成百上千异步 flow 争抢 */
      const replyIdsToHydrate = new Set<string>()
      /** 按会话聚合，便于每个会话一次性 prepend */
      const perChat = new Map<string, { chat: any; messages: Instance<typeof MessageModel>[] }>()

      rawMessages.forEach((raw) => {
        const m: MessageData & { notify_type?: NotifyType } = { ...raw }
        if (!m.message_id || !m.sender_id) return

        if (!m.chat_cid) {
          if (m.chat_id != null) {
            const groupChat = chatStore.byId(m.chat_id)
            if (groupChat) m.chat_cid = groupChat.chat_id
          }
          if (!m.chat_cid) {
            const other_id = currentUser.id === m.sender_id ? m.receiver_id : m.sender_id
            if (!other_id) return
            m.chat_cid = genPrivacyChatID(currentUser.id, other_id).chat_id
          }
        }

        const chat = chatStore.getChatByMessageRef(m) ?? chatStore.getChat(m.chat_cid)
        if (m.chat_id == null && chat?.id != null) {
          m.chat_id = chat.id
        }

        // 与原 messageCreatedHandle 一致：无论会话是否已存在，消息都写入内存并持久化，
        // 只有会话存在时才更新其渲染列表（避免会话尚未下发时丢消息）。
        const message = putMessage(m)
        if (!message) return
        if (!m.notify_type) {
          pendingSqLite.push(ensureMessageData(m) as MessageData)
        }

        const rid = message.reply_msg_id
        if (rid) replyIdsToHydrate.add(String(rid))

        if (!chat) return

        let bucket = perChat.get(m.chat_cid)
        if (!bucket) {
          bucket = { chat, messages: [] }
          perChat.set(m.chat_cid, bucket)
        }
        bucket.messages.push(message)
      })

      perChat.forEach(({ chat, messages }) => {
        if (chat.deleted) {
          chatStore.reviveChat(chat)
        }
        chat.prependUnreadSummaryBatch(messages)
        // 批量入列后按会话裁剪，非当前会话只留最近 N 条
        trimInactiveChatMessages(chat)
      })

      flushPackedSideEffects(pendingSqLite, replyIdsToHydrate)
    }

    /**
     * GAME_CHAT_UNREAD_SUMMARY 单个游戏群的未读处理：写入内存并按会话一次性 prepend，
     * SQLite 写入与回复链补齐通过传入的累积容器延后批量执行。返回本群实际处理的消息条数（用于分片预算）。
     */
    function applyGameChatUnreadItem(
      item: GameChatUnreadSummaryData["items"][0],
      pendingSqLite: MessageData[],
      replyIdsToHydrate: Set<string>,
    ): number {
      const { chatStore, userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { currentUser } = userStore
      const chat = chatStore.byId(item.chat_id) // 数字id的会话
      if (!chat) return 0

      if (item.unread_count > 0) {
        chat.addPackedUnreadHint(item.unread_count)
      }
      if (item.unread_count >= (item.messages?.length || 0) && chat.chat_id) {
        chat.clearMessages()
        deleteMessagesByChatID(chat.chat_id).catch((error) => {})
      }

      const batch = item.messages
      if (!batch?.length) return 0

      const messagesForPrepend: Instance<typeof MessageModel>[] = []

      batch.forEach((raw) => {
        const m: MessageData & { notify_type?: NotifyType } = { ...raw }
        if (!m.chat_cid) {
          m.chat_cid = chat.chat_id
        }
        if (m.chat_id == null && chat.id != null) {
          m.chat_id = chat.id
        }

        if (!m.message_id || !m.sender_id) {
          return
        }

        if (!m.chat_cid) {
          const other_id = currentUser.id === m.sender_id ? m.receiver_id : m.sender_id
          if (!other_id) return
          m.chat_cid = genPrivacyChatID(currentUser.id, other_id).chat_id
        }

        const message = putMessage(m)
        if (!message) return
        if (!m.notify_type) {
          pendingSqLite.push(ensureMessageData(m) as MessageData)
        }

        const rid = message.reply_msg_id
        if (rid) replyIdsToHydrate.add(String(rid))

        messagesForPrepend.push(message)
      })

      if (chat.deleted) {
        chatStore.reviveChat(chat)
      }

      chat.prependUnreadSummaryBatch(messagesForPrepend)
      // 未读摘要入列后裁剪，非当前会话只留最近 N 条（被裁未读已折算进 packedUnreadHint，角标不变）
      trimInactiveChatMessages(chat)
      return batch.length
    }

    /**
     * 处理 GAME_CHAT_UNREAD_SUMMARY 的一个分片：从 startIndex 起按消息预算处理若干游戏群，返回下一个待处理下标。
     * 需暴露为 action：分片是用 requestAnimationFrame 跨帧调度的，回调已脱离原 action 调用栈，
     * 必须经 self.* 重新进入 action 才能安全修改 MST（protected 模式下直接 putMessage 会抛错）。
     */
    function runGameChatUnreadChunk(
      items: GameChatUnreadSummaryData["items"],
      startIndex: number,
    ): number {
      /** 每帧独立累积，写入随帧及时下发并保持内存有界 */
      const pendingSqLite: MessageData[] = []
      const replyIdsToHydrate = new Set<string>()
      let index = startIndex
      let processed = 0

      while (index < items.length && processed < GAME_UNREAD_MESSAGE_CHUNK_BUDGET) {
        processed += applyGameChatUnreadItem(items[index], pendingSqLite, replyIdsToHydrate)
        index++
      }

      flushPackedSideEffects(pendingSqLite, replyIdsToHydrate)
      return index
    }

    /**
     * GAME_CHAT_UNREAD_SUMMARY：`chat_id` 为会话 ID，`unread_count` 为该会话未读总数，
     * `messages` 均为该会话未读消息。游戏群多时一次下发的群和消息都很多，整段同步处理会卡顿，
     * 这里按每帧消息条数预算把若干游戏群分片处理，超出预算就 requestAnimationFrame 让出主线程；
     * 小数据量首帧即同步处理完，不引入额外延迟。
     */
    function onGameChatUnreadSummary({ data }: Event<GameChatUnreadSummaryData>) {
      const items = data?.items
      if (!items?.length) return

      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      if (!chatStore.chatListHydrated) {
        chatStore.enqueuePendingGameChatUnreadSummary(data)
        return
      }

      const scheduleFrom = (startIndex: number) => {
        // 经 self.* 调用以确保（尤其是 rAF 延后的）分片在 action 上下文内执行；
        // 同一 .actions 块内 self 尚未带上本动作的类型，故 as any（运行时实例上已挂载）
        const nextIndex = (self as any).runGameChatUnreadChunk(items, startIndex)
        if (nextIndex < items.length) {
          requestAnimationFrame(() => scheduleFrom(nextIndex))
        }
      }

      scheduleFrom(0)
    }

    function onMessageSearched(data: MessageData) {
      // console.log("onMessageSearched", data)
    }
    // 消息状态变化(更新整条消息)
    function onMessageUpdated({ data }: Event<MessageData>) {
      const { message_id } = data

      const cM = self.messages.get(message_id)
      if (cM) {
        cM.update(data)
      }
      updateMessageByMessageID({ cM: data })
    }
    // 更新消息状态
    function onMessageStatusUpdated({
      data: { message_id, status, id },
    }: Event<MessageStatusUpdatedData>) {
      const cM = self.messages.get(message_id)
      if (cM) {
        cM.update({ id, status })
      }
      updateMessageByMessageID({ cM: { message_id, status } })
    }
    // 批量更新
    function onMessageBatchStatusUpdated({
      data: { items },
    }: Event<MessageBatchStatusUpdatedData>) {
      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      items.forEach(({ message_id, id, status }) => {
        const cM = self.messages.get(message_id)
        if (cM) {
          cM.update({ id, status })
          // 游戏群消息不从 SQLite 读回（进群直接拉服务端最新一屏），状态只更新内存、跳过落库
          // （item 无 chat_id，仅能对在内存的消息判断；不在内存的低频走原落库，保证私聊/普通群状态持久化）
          const chat = chatStore.getChat(cM.messageChatID)
          if (chat?.isGameGroup) return
        }
        updateMessageByMessageID({ cM: { message_id, status } })
      })
    }
    function onMessageBatchReaded({ data: { items } }: Event<MessageBatchReadedData>) {
      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      items.forEach(({ message_id, id, read_ids, chat_id }) => {
        const cM = self.messages.get(message_id)
        if (cM) {
          cM.update({ id, read_ids })
        }
        // 游戏群万级未读时，已读回执(event 80)会被服务端洪水式逐条下发；
        // 游戏群消息本就不从 SQLite 读回，逐条 UPDATE 只造成数千并发写放大（登录后卡顿/内存尖峰），
        // 故游戏群回执只更新内存、跳过落库。按 item.chat_id 判断，消息不在内存（洪水主场景）也能命中
        const chat = chat_id != null ? chatStore.byId(chat_id) : undefined
        if (chat?.isGameGroup) return
        updateMessageByMessageID({ cM: { message_id, read_ids } })
      })
    }
    function getChatByMessageID(messageID: string) {
      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const message = self.messages.get(messageID)
      const chat_id = message.messageChatID
      return chatStore.getChat(chat_id)
    }
    function handleMessageDeleted({ message_id }: { message_id: string; id: number }) {
      const cM = self.messages.get(message_id)
      if (cM) {
        const chat = getChatByMessageID(message_id)
        chat?.onMessageDeleted({ message_id })
        detach(cM)
        self.messages.delete(message_id)
      }
      deleteMessageByMessageID({ cM: { message_id } })
    }
    function handleMessagesBatchDeleted(items: { message_id?: string; id?: number }[]) {
      if (!items?.length) return

      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const messageIds = Array.from(
        new Set(items.map(({ message_id }) => message_id).filter(Boolean) as string[]),
      )
      if (!messageIds.length) return

      const chatMessageIds = new Map<string, Set<string>>()

      messageIds.forEach((message_id) => {
        const cM = self.messages.get(message_id)
        if (!cM) return

        const chat_cid = cM.chat_cid || cM.messageChatID
        if (!chat_cid) return

        if (!chatMessageIds.has(chat_cid)) {
          chatMessageIds.set(chat_cid, new Set())
        }
        chatMessageIds.get(chat_cid)!.add(message_id)

        detach(cM)
        self.messages.delete(message_id)
      })

      chatMessageIds.forEach((ids, chat_cid) => {
        const chat = chatStore.getChat(chat_cid)
        chat?.onMessagesBatchDeleted(ids)
      })

      deleteMessagesByMessageIDs(messageIds).catch(() => {})
    }
    function onMessageDeleted({ data: { message_id, id } }: Event<MessageDeleteData>) {
      handleMessageDeleted({ message_id, id })
    }
    function onMessageBatchDeleted({ data: { items } }: Event<MessageBatchDeleteData>) {
      handleMessagesBatchDeleted(items)
    }
    function formatMessageToMessageData(message: Message) {
      return {
        message_id: message.message_id,
        content: message.content,
        sender_id: message.sender?.id,
        sender_name: message.sender?.name || "",
        receiver_id: message.receiver_id ? message.receiver_id : (message.receiver ? message.receiver.id : null),
        receiver_name: message.receiver_name ? message.receiver_name : (message.receiver ? message.receiver.name : ""),
        status: message.status,
        type: message.type,
        chat_id: message.chat_id,
        chat_cid: message.chat_cid,
        attach: message.attach,
        created: message.created.getTime(),
        updated: message.updated.getTime(),
        mention_ids: message.mention_ids,
        reply_msg_id: message.reply_msg_id,
        forward_msg_id: message.forward_msg_id,
        forward_user_id: message.forward_user_id,
        forward_name: message.forward_name,
        burning_timer: message.burning_timer || 0,
      }
    }
    function sendBatchMessage(messages: Message) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<MessageBatchNewData>(EventNames.MESSAGE_BATCH_NEW, {
        items: messages.map((message) => formatMessageToMessageData(message)),
      })
      eventStore.publish(evt, { flags: FLAG_ACK })
      messageSentSound.play(() => {
        //
      })
    }
    function senddeleteMessageByMessageID(message: Message, isLocal = false) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      if (!isLocal) {
        const evt = new Event<MessageDeleteData>(EventNames.MESSAGE_DELETE, {
          id: message.id,
          message_id: message.message_id,
        })
        eventStore.publish(evt, { flags: FLAG_ACK })
      }
      handleMessageDeleted({ message_id: message.message_id, id: message.id })
    }
    function sendImagesMessage(
      message: Message,
      successCallback?: (msg: Message) => void,
      errorCallback?: (e: Error) => void,
    ) {
      message
        .uploadMultipleImages()
        .then(() => {
          const m = {
            ...message,
            attach: {
              imgs: message.attach.imgs.map((img) => {
                return {
                  url: img.url,
                  filename: img.filename,
                  type: img.type,
                  size: img.size,
                  length: 0,
                }
              }),
            },
          }
          sendMessage(m)
          successCallback && successCallback(m)
        })
        .catch((e) => {
          errorCallback && errorCallback(e)
        })
    }
    function sendImageMessage(
      message: Message,
      successCallback?: (msg: Message) => void,
      errorCallback?: (e: Error) => void,
    ) {
      message
        .upload()
        .then(() => {
          sendMessage(message)
          successCallback && successCallback(message)
        })
        .catch((e) => {
          errorCallback && errorCallback(e)
        })
    }
    function sendVoiceMessage(
      message: Message,
      successCallback?: (msg: Message) => void,
      errorCallback?: (e: Error) => void,
    ) {
      message
        .upload()
        .then(() => {
          sendMessage(message)
          successCallback && successCallback(message)
        })
        .catch((e) => {
          errorCallback && errorCallback(e)
        })
    }
    function sendVideoMessage(
      message: Message,
      successCallback?: (msg: Message) => void,
      errorCallback?: (e: Error) => void,
    ) {
      message
        .upload()
        .then(() => {
          sendMessage(message)
          successCallback && successCallback(message)
        })
        .catch((e) => {
          errorCallback && errorCallback(e)
        })
    }
    function sendFileMessage(
      message: Message,
      successCallback?: (msg: Message) => void,
      errorCallback?: (e: Error) => void,
    ) {
      message
        .upload()
        .then(() => {
          sendMessage(message)
          successCallback && successCallback(message)
        })
        .catch((e) => {
          errorCallback && errorCallback(e)
        })
    }
    function sendTextMessage(
      message: Message,
      successCallback?: (msg: Message) => void,
      errorCallback?: (e: Error) => void,
    ) {
      new Promise((resolve) => {
        sendMessage(message)
        resolve(message)
      })
        .then((m) => {
          successCallback && successCallback(m)
        })
        .catch((e) => {
          errorCallback && errorCallback(e)
        })
    }
    function sendMessageByMessageType(
      message: Message,
      successCallback?: (msg: Message) => void,
      errorCallback?: (err: Error) => void,
    ) {
      switch (message.type) {
        case MessageTypes.TEXT:
          sendTextMessage(message, successCallback, errorCallback)
          break
        case MessageTypes.SHARE_CARD:
          sendTextMessage(message, successCallback, errorCallback)
          break
        case MessageTypes.VOICE:
          sendVoiceMessage(message, successCallback, errorCallback)
          break
        case MessageTypes.IMAGE:
          sendImageMessage(message, successCallback, errorCallback)
          break
        case MessageTypes.VIDEO:
          sendVideoMessage(message, successCallback, errorCallback)
          break
        case MessageTypes.FILE:
          sendFileMessage(message, successCallback, errorCallback)
          break
        case MessageTypes.IMAGES:
          sendImagesMessage(message, successCallback, errorCallback)
          break
        default:
          break
      }
    }
    // 发送消息
    function sendMessage({ message_id }: Message) {
      const { eventStore, channelStore, messageStore } = getRoot<Instance<typeof RootStoreModel>>(
        self,
      )
      const message = messageStore.messages.get(message_id)
      const now = Date.now()
      message.update({
        created: now,
        updated: now,
      })
      self.sendMessageTimeoutMessageIdMap[message.message_id] = setTimeout(() => {
        message.setStatus(MessageStatus.FAILED)
        channelStore.removeMessageNewFromSendqByMessageID(message.message_id)
      }, 10000)
      const evt = new Event<MessageData>(
        EventNames.MESSAGE_NEW,
        formatMessageToMessageData(message),
      )
      eventStore.publish(evt, { flags: FLAG_ACK })
    }
    function remove(message_id: string) {
      self.messages.delete(message_id)
    }
    // （trimInactiveChatMessages 定义见上方批量路径处，与 remove 同块导出）
    let disposer
    const afterCreate = () => {
      disposer = autorun(() => {
        const rootStore = getRoot<Instance<typeof RootStoreModel>>(self)
        if (rootStore.appState === "active") {
          syncAppBadge(self.unread || 0)
        }
      })
    }
    const beforeDestory = () => {
      disposer()
    }
    return {
      afterCreate,
      beforeDestory,
      addMessage,
      clear,
      repairChatMessageUsers,
      remove,
      trimInactiveChatMessages,
      onMessageStatusUpdated,
      onMessageBatchStatusUpdated,
      onMessageBatchReaded,
      onMessageDeleted,
      formatMessageToMessageData,
      sendBatchMessage,
      sendMessageByMessageType,
      sendMessage,
      senddeleteMessageByMessageID,
      onHistoryMessageCreated,
      onLastMessageCreated,
      onMessageCreated,
      onMessageSearched,
      handleMessageDeleted,
      handleMessagesBatchDeleted,
      getMessage,
      hasMessageInDB,
      putMessageInDB,
      onMessageBatchDeleted,
      onMessageUpdated,
      onPackedMessagesCreated,
      runGameChatUnreadChunk,
      onGameChatUnreadSummary,
    }
  })

type MessageStoreType = Instance<typeof MessageStoreModel>
export interface MessageStore extends MessageStoreType {}
type MessageStoreSnapshotType = SnapshotOut<typeof MessageStoreModel>
export interface MessageStoreSnapshot extends MessageStoreSnapshotType {}
export const createMessageStoreDefaultModel = () => types.optional(MessageStoreModel, {})
